function normalizeIdSegment(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function createElementIdFactory(prefix) {
  const basePrefix = normalizeIdSegment(prefix || "element");

  return (...segments) => {
    const normalizedSegments = segments
      .map((segment) => normalizeIdSegment(segment))
      .filter(Boolean);

    return [basePrefix, ...normalizedSegments].join("-");
  };
}

export function createReusableTableElementIds(createId, tableScope = "main") {
  const makeScopedId = (...segments) => createId(tableScope, ...segments);

  return {
    prefix: createId(),
    container: createId("container"),
    table: makeScopedId("table"),
    thead: makeScopedId("thead"),
    tbody: makeScopedId("tbody"),
    pagination: makeScopedId("pagination"),
    paginationFirst: makeScopedId("pagination", "first"),
    paginationPrev: makeScopedId("pagination", "prev"),
    paginationStatus: makeScopedId("pagination", "status"),
    paginationPageInput: makeScopedId("pagination", "page", "input"),
    paginationNext: makeScopedId("pagination", "next"),
    paginationLast: makeScopedId("pagination", "last"),
    pageSizeInput: makeScopedId("page", "size", "input"),
    pageSizeInstance: makeScopedId("page", "size", "instance"),
    headerRow: (headerGroup) => createId("header", "row", headerGroup.id),
    headerCell: (header) => createId("header", "cell", header.id),
    row: (row) => makeScopedId("row", row.id),
    cell: (cell) => makeScopedId("cell", cell.id),
  };
}

export function createWithholdingProofingElementIds(createId) {
  return {
    propertyKey: createId("property", "key", "input"),
    userName: createId("user", "name", "input"),
    location: createId("location", "select"),
    cityscope: createId("cityscope", "select"),
    holdReason: createId("hold", "reason", "select"),
    primaryClassification: createId("primary", "classification", "select"),
    searchButton: createId("search", "button"),
    refreshButton: createId("refresh", "button"),
  };
}

export function createSLMCategoriesElementIds(createId) {
  return {
    ...createReusableTableElementIds(createId),
    groupFilter: createId("group", "filter", "select"),
    addButton: createId("add", "button"),
  };
}

export function createSLMSuburbsElementIds(createId) {
  return {
    ...createReusableTableElementIds(createId),
    suburbFilter: createId("suburb", "filter", "input"),
    postcodeFilter: createId("postcode", "filter", "input"),
    searchButton: createId("search", "button"),
    addButton: createId("add", "button"),
  };
}

export function createSLMRCASuppressionsElementIds(createId) {
  return {
    ...createReusableTableElementIds(createId),
    refreshButton: createId("refresh", "button"),
  };
}

export function createSLMEntityDetailElementIds(createId) {
  const contactsCreateId = (...segments) => createId("contacts", ...segments);

  return {
    root: createId("root"),
    form: createId("form"),
    title: createId("title"),
    saveButton: createId("save", "button"),
    previousButton: createId("browse", "previous", "button"),
    nextButton: createId("browse", "next", "button"),
    browseIndexInput: createId("browse", "index", "input"),
    nameInput: createId("field", "name", "input"),
    phoneExtInput: createId("field", "phone", "ext", "input"),
    phoneNoInput: createId("field", "phone", "number", "input"),
    faxExtInput: createId("field", "fax", "ext", "input"),
    faxNoInput: createId("field", "fax", "number", "input"),
    webInput: createId("field", "web", "input"),
    emailInput: createId("field", "email", "input"),
    physStreetInput: createId("field", "physical", "street", "input"),
    physSuburbInput: createId("field", "physical", "suburb", "input"),
    physStateSelect: createId("field", "physical", "state", "select"),
    physCodeInput: createId("field", "physical", "postcode", "input"),
    mailStreetInput: createId("field", "mail", "street", "input"),
    mailSuburbInput: createId("field", "mail", "suburb", "input"),
    mailStateSelect: createId("field", "mail", "state", "select"),
    mailCodeInput: createId("field", "mail", "postcode", "input"),
    notesInput: createId("field", "notes", "input"),
    contactsSection: createId("contacts", "section"),
    contactsAddButton: createId("contacts", "add", "button"),
    contactsTable: createReusableTableElementIds(contactsCreateId),
  };
}

export function createSLMOwnerDetailElementIds(createId) {
  const contactsCreateId = (...segments) => createId("contacts", ...segments);

  return {
    root: createId("root"),
    form: createId("form"),
    title: createId("title"),
    previousButton: createId("browse", "previous", "button"),
    nextButton: createId("browse", "next", "button"),
    browseIndexInput: createId("browse", "index", "input"),
    nameInput: createId("field", "name", "input"),
    phoneExtInput: createId("field", "phone", "ext", "input"),
    phoneNoInput: createId("field", "phone", "number", "input"),
    faxExtInput: createId("field", "fax", "ext", "input"),
    faxNoInput: createId("field", "fax", "number", "input"),
    webInput: createId("field", "web", "input"),
    emailInput: createId("field", "email", "input"),
    physStreetInput: createId("field", "physical", "street", "input"),
    physSuburbInput: createId("field", "physical", "suburb", "input"),
    physStateSelect: createId("field", "physical", "state", "select"),
    physCodeInput: createId("field", "physical", "postcode", "input"),
    mailStreetInput: createId("field", "mail", "street", "input"),
    mailSuburbInput: createId("field", "mail", "suburb", "input"),
    mailStateSelect: createId("field", "mail", "state", "select"),
    mailCodeInput: createId("field", "mail", "postcode", "input"),
    notesInput: createId("field", "notes", "input"),
    contactsSection: createId("contacts", "section"),
    contactsAddButton: createId("contacts", "add", "button"),
    contactsTable: createReusableTableElementIds(contactsCreateId),
  };
}

export function createSLMOwnerModalElementIds(createId) {
  return {
    container: createId("container"),
    form: createId("form"),
    searchLabel: createId("field", "search-owner", "label"),
    searchInput: createId("field", "search-owner", "input"),
    interstateCheckbox: createId("field", "search-interstate", "checkbox"),
    interstateLabel: createId("field", "search-interstate", "label"),
    searchButton: createId("action", "search", "button"),
    createOwnerButton: createId("action", "create-owner", "button"),
    editOwnerButton: createId("action", "edit-owner", "button"),
    addOwnerLinkButton: createId("action", "add-owner-link", "button"),
    ownerLabel: createId("field", "owner", "label"),
    ownerInput: createId("field", "owner", "input"),
    ownerInstance: createId("field", "owner", "instance"),
    ownerPhoneLabel: createId("field", "owner-phone", "label"),
    ownerPhoneInput: createId("field", "owner-phone", "input"),
    ownerLocaleLabel: createId("field", "owner-locale", "label"),
    ownerLocaleInput: createId("field", "owner-locale", "input"),
    contactLabel: createId("field", "contact", "label"),
    contactInput: createId("field", "contact", "input"),
    contactInstance: createId("field", "contact", "instance"),
    contactPhoneLabel: createId("field", "contact-phone", "label"),
    contactPhoneInput: createId("field", "contact-phone", "input"),
    contactEmailLabel: createId("field", "contact-email", "label"),
    contactEmailInput: createId("field", "contact-email", "input"),
    typeLabel: createId("field", "type", "label"),
    typeInput: createId("field", "type", "input"),
    typeInstance: createId("field", "type", "instance"),
    addContactButton: createId("action", "add-contact", "button"),
  };
}

export function createSLMVolumeFoliosListElementIds(createId) {
  const tableCreateId = (...segments) => createId("table", ...segments);

  return {
    container: createId("container"),
    refreshSortButton: createId("action", "refresh-sort", "button"),
    addButton: createId("action", "add", "button"),
    table: createReusableTableElementIds(tableCreateId),
  };
}

export function createSLMVolumeFoliosModalElementIds(createId) {
  return {
    modal: createId("modal"),
    title: createId("title"),
  };
}

export function createSLMTitleDetailsListElementIds(createId) {
  const tableCreateId = (...segments) => createId("table", ...segments);

  return {
    container: createId("container"),
    addButton: createId("action", "add", "button"),
    deleteAllButton: createId("action", "delete-all", "button"),
    table: createReusableTableElementIds(tableCreateId),
  };
}

export function createSLMTitleDetailsModalElementIds(createId) {
  return {
    modal: createId("modal"),
    title: createId("title"),
  };
}